'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { CardRenderContext } from '../../tool-config-types';
import type { ChatObjectNavigationTarget } from '../../hitl-types';
import { PortalObjectReferenceCard } from '../shared/PortalObjectReferenceCard';
import { ToolInlineStatusLine } from '../shared/ToolInlineStatusLine';
import type { ObjectUpdateProposal } from '@terra/shared-dto-chat-types';

type ObjectUpdateCardProps = {
  ctx: CardRenderContext;
  findingNavigationTarget: Extract<
    ChatObjectNavigationTarget,
    { type: 'finding' }
  >;
};

function ObjectUpdateCard({
  ctx,
  findingNavigationTarget,
}: ObjectUpdateCardProps) {
  const t = useTranslations('designSystem.chat.objectReference');
  const findingDraftLabel = t('findingDraft');
  const status = ctx.toolCallData.status;
  const isRunning = status === 'started' || status === 'in-progress';
  const isError = status === 'error' || Boolean(ctx.toolCallData.error);
  const canShowObjectReference =
    status === 'completed' && Boolean(findingNavigationTarget);

  if (isRunning) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.runningText ?? ctx.labelPresent}
      />
    );
  }

  if (isError) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.toolCallData.error ?? ctx.labelPast}
        isError
      />
    );
  }

  if (!canShowObjectReference) {
    return null;
  }

  return (
    <PortalObjectReferenceCard
      objectType="finding"
      objectId={findingNavigationTarget.id}
      title={findingNavigationTarget.label ?? findingDraftLabel}
      prefix={t('updatedFinding')}
      onOpenObject={ctx.onOpenObject}
      navigationTarget={findingNavigationTarget}
    />
  );
}

function ObjectUpdateCardContainer({ ctx }: { ctx: CardRenderContext }) {
  const t = useTranslations('designSystem.chat.objectReference');
  const findingDraftLabel = t('findingDraft');
  const proposal = readProposal(
    ctx.toolCallData.output,
    ctx.toolCallData.input,
  );
  if (!proposal || proposal.objectKind !== 'Finding') {
    return null;
  }
  const patchTitle =
    typeof proposal.patch.title === 'string' && proposal.patch.title.length > 0
      ? proposal.patch.title
      : undefined;
  const linkedReference = ctx.linkedObjectReference;
  const findingNavigationTarget: Extract<
    ChatObjectNavigationTarget,
    { type: 'finding' }
  > = {
    type: 'finding',
    id:
      linkedReference?.type === 'finding'
        ? linkedReference.id
        : proposal.objectId,
    label:
      linkedReference?.type === 'finding'
        ? linkedReference.label
        : (patchTitle ?? findingDraftLabel),
    mode: 'edit',
  };

  return (
    <ObjectUpdateCard
      ctx={ctx}
      findingNavigationTarget={findingNavigationTarget}
    />
  );
}

export const renderObjectUpdateCard = (ctx: CardRenderContext): ReactNode => (
  <ObjectUpdateCardContainer ctx={ctx} />
);

const MAX_PROPOSAL_PARSE_DEPTH = 8;

function readProposal(
  output: unknown,
  input: unknown,
): ObjectUpdateProposal | null {
  return readPayload(output) ?? readPayload(input);
}

function toObjectUpdateProposal(
  record: Record<string, unknown>,
): ObjectUpdateProposal | null {
  if (record.type !== 'object_update_proposal') {
    return null;
  }
  const objectKind = record.objectKind;
  const objectId = record.objectId;
  const patch = record.patch;
  if (
    typeof objectKind !== 'string' ||
    typeof objectId !== 'string' ||
    objectId.length === 0 ||
    !patch ||
    typeof patch !== 'object' ||
    Array.isArray(patch) ||
    Object.keys(patch).length === 0
  ) {
    return null;
  }
  return {
    type: 'object_update_proposal',
    objectKind,
    objectId,
    patch: patch as Record<string, unknown>,
    reason: typeof record.reason === 'string' ? record.reason : '',
  };
}

function readPayload(value: unknown, depth = 0): ObjectUpdateProposal | null {
  if (depth > MAX_PROPOSAL_PARSE_DEPTH) {
    return null;
  }
  const parsed = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const direct = toObjectUpdateProposal(record);
  if (direct) {
    return direct;
  }
  const nested = record.output ?? record.result ?? record.content;
  if (nested && nested !== parsed) {
    return readPayload(nested, depth + 1);
  }
  return null;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
